import {
  createRootRouteWithContext,
  createRoute,
  Outlet,
  redirect,
  Link,
  useNavigate,
  useRouterState,
  lazyRouteComponent,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { startPush } from '@/lib/push';
import { WelcomeTourMount } from '@/components/WelcomeTour';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Landing from '@/pages/Landing';
import MobileIntro, { hasSeenIntro } from '@/pages/MobileIntro';
import Login from '@/pages/Login';
import { isNative } from '@/lib/api-base';
import Register from '@/pages/Register';
import Pricing from '@/pages/Pricing';
import Solution from '@/pages/Solution';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import GoogleCallback from '@/pages/GoogleCallback';
import { UploadIndicator } from '@/components/upload/UploadIndicator';
import { QuotaNudgeMount } from '@/components/billing/QuotaExceededDialog';
import { GlobalUpgradeFlow } from '@/components/billing/GlobalUpgradeFlow';
import { GlobalLoadingGate } from '@/components/ui/GlobalLoadingGate';
import { enforceHostSplit } from '@/lib/host-split';
import { useLoadingGate } from '@/store/loadingStore';

/**
 * Rotas pesadas entram sob demanda.
 *
 * Estava tudo num pacote só: quem abria a landing baixava o editor, o
 * visualizador público e o Babylon.js inteiro junto — 9 MB para ver uma
 * página de marketing que não usa nada disso. Era o motivo de o site
 * demorar a responder e de cada tour embutido levar tanto para aparecer.
 *
 * A landing, o login e as páginas institucionais continuam no pacote
 * principal: são a porta de entrada, e um segundo pedido antes de pintar a
 * primeira tela trocaria um problema por outro. O resto — tudo que só
 * existe depois de entrar, mais o visualizador — chega quando a rota for
 * realmente visitada.
 */
const Dashboard = lazyRouteComponent(() => import('@/pages/Dashboard'));
const Editor = lazyRouteComponent(() => import('@/pages/Editor'));
const PublicTour = lazyRouteComponent(() => import('@/pages/PublicTour'));
const PublicLanding = lazyRouteComponent(() => import('@/pages/PublicLanding'));
const DemoEditor = lazyRouteComponent(() => import('@/pages/DemoEditor'));
const Analytics = lazyRouteComponent(() => import('@/pages/Analytics'));
const AnalyticsOverview = lazyRouteComponent(() => import('@/pages/AnalyticsOverview'));
const Settings = lazyRouteComponent(() => import('@/pages/Settings'));
const Billing = lazyRouteComponent(() => import('@/pages/Billing'));
const Leads = lazyRouteComponent(() => import('@/pages/Leads'));
const Help = lazyRouteComponent(() => import('@/pages/Help'));
const Sponsor = lazyRouteComponent(() => import('@/pages/Sponsor'));
const VrLab = lazyRouteComponent(() => import('@/pages/VrLab'));
const TransitionsLab = lazyRouteComponent(() => import('@/pages/TransitionsLab'));
const Admin = lazyRouteComponent(() => import('@/pages/Admin'));
const Status = lazyRouteComponent(() => import('@/pages/Status'));


interface RouterContext {
  queryClient: QueryClient;
}

/**
 * 404 — any unknown path lands here instead of a blank/stuck screen. Routes the
 * user back to a safe place (dashboard if logged in, else home/login). This is
 * the global "never get stuck" backstop for navigation.
 */
function NotFound() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const dest = isAuthenticated ? '/dashboard' : '/';
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-5 px-6 text-center bg-[var(--color-base)] text-[var(--color-text)]">
      <img src="/logo.png" alt="" className="w-16 h-16 object-contain opacity-90" />
      <div className="text-5xl font-bold tracking-tight">404</div>
      <p className="text-[var(--color-text-muted)] max-w-sm">
        {t('common:notFound.message')}
      </p>
      <div className="flex items-center gap-3 flex-wrap justify-center">
        <Link
          to={dest}
          className="inline-flex items-center h-11 px-6 rounded-xl bg-[var(--color-primary)] text-white font-semibold hover:opacity-90 transition-opacity"
        >
          {isAuthenticated ? t('common:notFound.goDashboard') : t('common:notFound.goHome')}
        </Link>
        <Link
          to="/status"
          className="inline-flex items-center h-11 px-6 rounded-xl border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface)] transition-colors"
        >
          {t('common:notFound.systemStatus')}
        </Link>
      </div>
    </div>
  );
}

// Root route
const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFound,
});

function RootComponent() {
  const { checkAuth, isLoading, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  /**
   * O host certo a CADA navegação, não só ao abrir.
   *
   * O redirecionamento do main.tsx roda uma vez, no carregamento. Mas um
   * <Link to="/login"> no site é navegação interna: a página não recarrega,
   * então nada dispararia — e a pessoa acabaria logando em my360.site,
   * gravando o token na origem errada.
   *
   * Aqui é rede de segurança. Os botões principais já apontam direto para
   * o app; este efeito cobre os que escaparem e os que vierem depois.
   */
  useEffect(() => {
    enforceHostSplit();
  }, [pathname]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Push starts only once there IS an account: a device token with nobody
  // attached is a token nobody can ever send to. Asking for the permission
  // before login would also spend the one prompt Android gives us on someone
  // who hasn't seen the product yet.
  useEffect(() => {
    if (!isAuthenticated) return;
    void startPush((path) => void navigate({ to: path }));
  }, [isAuthenticated, navigate]);

  // The landing renders through the auth check instead of behind a loader.
  // It opens with its own curtain intro, and stacking the auth loader in
  // front of it meant the visitor watched two loading phases back to back:
  // a sweeping bar, then a counter. The landing needs no session to paint —
  // it only swaps one nav button once `isAuthenticated` settles.
  const onLanding = typeof window !== 'undefined' && window.location.pathname === '/';

  // O loader do auth vive no gate global: se a página seguinte também
  // carrega algo (projeto, cena), é o MESMO loader que segue segurando a
  // tela — nunca um fecha pra outro abrir.
  const authPending = isLoading && !onLanding;
  useLoadingGate('auth', authPending);

  // O gate fica montado SEMPRE (fora do condicional): é a mesma instância
  // que cobre auth e o que vier depois, então o fade de saída existe e
  // nunca há troca de loader no meio.
  return (
    <>
      <GlobalLoadingGate />
      {authPending ? null : (
        <>
          <Outlet />
          {/* Montado na raiz para que a decisão de mostrar exista num lugar só:
              espalhada por várias telas, ela acaba aparecendo duas vezes ou
              nenhuma. Só consulta o servidor depois que há sessão. */}
          <WelcomeTourMount enabled={isAuthenticated} />
          <UploadIndicator />
          {/* Mount global do nudge de cota — qualquer hook (useStorageGuard) pode
              dispará-lo via useQuotaStore.openStorageNudge(). Fica aqui pra
              garantir que aparece sobre qualquer rota / modal. */}
          <QuotaNudgeMount />
      {/* Mount global do fluxo de upgrade — picker + checkout. Qualquer
          CTA "fazer upgrade" no app autenticado chama
          useUpgradeStore.openUpgrade() em vez de navegar pra /pricing
          (que é página de marketing pra prospect). Auto-noop se não auth. */}
          <GlobalUpgradeFlow />
        </>
      )}
    </>
  );
}

// Auth layout component
function AuthLayout() {
  // Auth pages render their own full-screen layout (split form + ambient pane)
  return <Outlet />;
}

// Auth layout
const authLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth',
  beforeLoad: () => {
    const { isAuthenticated } = useAuthStore.getState();
    if (isAuthenticated) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: AuthLayout,
});

// Protected layout
const protectedLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  beforeLoad: () => {
    const { isAuthenticated } = useAuthStore.getState();
    if (!isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: () => <Outlet />,
});

// Login page
const loginRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/login',
  component: Login,
});

// Register page
const registerRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/register',
  component: Register,
});

// Dashboard
const dashboardRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/dashboard',
  component: Dashboard,
});

// Project editor
const editorRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/editor/$projectId',
  component: Editor,
});

// Analytics overview (all projects)
const analyticsOverviewRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/analytics',
  component: AnalyticsOverview,
});

// Project analytics
const analyticsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/analytics/$projectId',
  component: Analytics,
});

// Settings
const settingsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/settings',
  component: Settings,
});

// Billing — assinatura, faturas, packs de storage. Lugar dedicado
// (saiu de /settings?tab=plan/invoices) pra reduzir atrito de compra.
const billingRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/billing',
  component: Billing,
});

// Leads — inbox de contatos capturados via ContactForm hotspots
const leadsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/leads',
  component: Leads,
});

// Help — guia, FAQ, tutoriais, suporte (substitui o antigo HelpModal/drawer)
const helpRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/help',
  component: Help,
});

// Redirect de compat: saúde da cobrança, estúdio de IA e custos agora
// vivem como ABAS do /admin — deep links antigos caem na aba certa.
function RedirectToAdminTab({ tab }: { tab: string }) {
  useEffect(() => {
    window.location.replace(`/admin?tab=${tab}`);
  }, [tab]);
  return null;
}

// Platform staff: billing pipeline health (agora aba do /admin)
const adminBillingHealthRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/admin/billing-health',
  component: () => <RedirectToAdminTab tab="billingHealth" />,
});

// Sponsor / referral program
const sponsorRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/sponsor',
  component: Sponsor,
});

// Bancada de testes VR — pública de propósito (testar no browser do Quest
// sem login). Não indexa e não toca em dados de conta.
const vrLabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lab/vr',
  component: VrLab,
});

// Bancada de transições de cena (dentro da cena 3D, válidas em VR) —
// página de julgamento; o aprovado migra pro BabylonViewer como opção.
const transitionsLabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lab/transicoes',
  component: TransitionsLab,
});

// Platform admin panel
const adminRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/admin',
  component: Admin,
});

// AI staging studio — agora aba do /admin.
const adminStagingRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/admin/staging',
  component: () => <RedirectToAdminTab tab="staging" />,
});

// Custos e margem — agora aba do /admin.
const adminCostsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/admin/costs',
  component: () => <RedirectToAdminTab tab="costs" />,
});

// Pricing — página de marketing PÚBLICA (só pra prospect). Logado é
// redirecionado pra /billing (que tem o card de plano + PlanPickerDialog
// integrados). Política: a partir do dashboard, qualquer "fazer upgrade"
// abre PlanPickerDialog via useUpgradeStore.openUpgrade() — nunca rota.
const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing',
  beforeLoad: () => {
    const { isAuthenticated } = useAuthStore.getState();
    if (isAuthenticated) {
      throw redirect({ to: '/billing' });

    }
  },
  component: Pricing,
});

// Páginas de segmento (/solucoes/:segment) — landing pages de captação,
// públicas e indexáveis, uma por dor de mercado.
const solutionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/solucoes/$segment',
  component: Solution,
});

// Public tour view (unique shareId link)
const tourRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$shareId',
  component: PublicTour,
});

/**
 * A landing de lançamento — mesma raiz pública do tour, endereço próprio.
 *
 * /l/ e não /p/: são páginas diferentes para públicos diferentes. O /p/ é o
 * tour, para quem já quer olhar; o /l/ é a apresentação, para quem ainda
 * está decidindo se vale a visita.
 */
const publicLandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/l/$shareId',
  component: PublicLanding,
});

// Public self-driving editor demo — the real viewer + real editor UI
// operating themselves on the public demo tour. Embedded by the landing
// page as an iframe; no auth, nothing persists.
const demoEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/demo/editor',
  component: DemoEditor,
});

// Public status page — no auth required
const statusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/status',
  component: Status,
});

// Public legal pages — no auth required (footer links on the landing)
const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: Privacy,
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: Terms,
});

// Forgot password — public, no auth required
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPassword,
});

// Reset password — public, reads ?token= from query
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPassword,
});

// Verify email — public, reads ?token= from query
const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email',
  component: VerifyEmail,
});

// Google sign-in landing — public, reads ?code / ?error from query. Registered
// on the ROOT (not the auth layout) on purpose: the layout bounces anyone
// already authenticated to /dashboard, which would abort the exchange.
const googleCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/google/callback',
  component: GoogleCallback,
});

// Landing route — branches based on platform.
//   Web (browser):    Landing.tsx (full marketing page)
//   Capacitor (app):  MobileIntro on first run, then /login direct on
//                     subsequent opens. Authed users always go straight
//                     to /dashboard.
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    const { isAuthenticated } = useAuthStore.getState();
    if (isAuthenticated) {
      throw redirect({ to: '/dashboard' });
    }
    // In Capacitor, skip the marketing landing page entirely. First
    // run shows the intro; subsequent runs jump straight to login.
    if (isNative()) {
      if (hasSeenIntro()) {
        throw redirect({ to: '/login' });
      }
      // Fall through — render MobileIntro (component switch below).
    }
  },
  component: () => (isNative() ? <MobileIntro /> : <Landing />),
});

export const routeTree = rootRoute.addChildren([
  landingRoute,
  pricingRoute,
  statusRoute,
  privacyRoute,
  termsRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  verifyEmailRoute,
  googleCallbackRoute,
  authLayout.addChildren([loginRoute, registerRoute]),
  protectedLayout.addChildren([dashboardRoute, editorRoute, analyticsOverviewRoute, analyticsRoute, settingsRoute, billingRoute, leadsRoute, helpRoute, adminBillingHealthRoute, sponsorRoute, adminRoute, adminStagingRoute, adminCostsRoute]),
  tourRoute,
  publicLandingRoute,
  demoEditorRoute,
  vrLabRoute,
  transitionsLabRoute,
  solutionRoute,
]);
